import {useEffect, useState} from "react";
import {Link} from "react-router";
import direDawnIcon from "@assets/diredawn_icon.png";
import {Button} from "@/components/ui/button.tsx";
import {AccessibleCampaign} from "@/auth/User.ts";
import {getMyCampaigns} from "@/api/UserApi.ts";
import UserMenu from "@/auth/UserMenu.tsx";
import CampaignCard from "@/campaigns/CampaignCard.tsx";

export default function CampaignSelectionView() {
    const [campaigns, setCampaigns] = useState<AccessibleCampaign[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        getMyCampaigns(controller.signal)
            .then(values => {
                setCampaigns(values);
                setError(false);
            })
            .catch(() => {
                if (!controller.signal.aborted) setError(true);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => controller.abort();
    }, [attempt]);

    return <div className="flex h-full min-h-0 flex-col bg-background">
        <title>Kampagne auswählen | DireDawn</title>
        <header className="flex h-12 shrink-0 items-center border-b border-border bg-surface/85 px-2 shadow-lg shadow-black/10 backdrop-blur-xl">
            <Link to="/" className="flex items-center gap-2 px-2" aria-label="DireDawn">
                <img src={direDawnIcon} className="size-7" alt=""/>
                <span className="font-semibold tracking-wide">DireDawn</span>
            </Link>
            <div className="grow"/>
            <UserMenu/>
        </header>
        <main className="min-h-0 grow overflow-auto p-6 md:p-10">
            <div className="mx-auto max-w-5xl space-y-6">
                <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-primary">Deine Abenteuer</p>
                    <h1 className="text-3xl font-semibold tracking-tight">Kampagne auswählen</h1>
                    <p className="mt-2 text-muted-foreground">Wähle den Kontext für deine nächste Sitzung.</p>
                </div>
                {loading && <p role="status" className="text-muted-foreground">Kampagnen werden geladen…</p>}
                {error && <div role="alert" className="flex items-center gap-3 text-red-400">
                    <span>Kampagnen konnten nicht geladen werden.</span>
                    <Button variant="outline" size="sm" onClick={() => {
                        setLoading(true);
                        setAttempt(value => value + 1);
                    }}>Erneut versuchen</Button>
                </div>}
                {!loading && !error && campaigns.length === 0 &&
                    <p className="text-muted-foreground">Dir ist noch keine Kampagne zugeordnet.</p>}
                {!error && <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {campaigns.map(campaign => <CampaignCard key={campaign.id} campaign={campaign}
                        to={`/campaigns/${campaign.slug}${campaign.role === "PLAYER" ? "/player" : ""}`}
                        badge={campaign.role === "GAME_MASTER" ? "Spielleiter" : "Spieler"}/>)}
                </div>}
            </div>
        </main>
    </div>;
}
